import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { refetchAllAppointmentsApis } from "../../api/Appointments/refetchAllAppointmentsApis";
import EmptyImage from "../../assets/images/icons/appointment-empty.svg";
import calender from "../../assets/images/icons/calendar.svg";
import PlusIcon from "../../assets/images/icons/plus-03.svg";
import close from "../../assets/images/icons/x_white.svg";
import {
  GeneralButton,
  GeneralCheckbox,
  InfiniteScroll,
  Modal,
} from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useFilter, useGetData } from "../../hooks";
import usePostData from "../../hooks/ReactQuery/usePostData";
import useAppMediaQuery from "../../hooks/window/useAppMediaQuery";
import {
  IAppointment,
  IAppointmentStatus,
} from "../../interface/appointmentInterfaces";
import { useToast } from "../../Services/ToastContext";
import { IsProfileCompleted } from "../../Share";
import {
  AppointmentCancelConfirm,
  AppointmentDetailsDrawer,
  AppointmentsCalendar,
  MyAppointmentCard,
  MyAppointmentCardLoader,
} from "./component";

const AppointmentsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [appointmentsData, setAppointmentsData] = useState<IAppointment[]>();
  const currentLocationParams = new URLSearchParams(location.search);
  const selectedDate = currentLocationParams.get("appointment-Date");
  const filterByStatus = currentLocationParams.get("filter"); 
  const { isMobileOrTablet } = useAppMediaQuery();

  const [showSelect, setShowSelect] = useState<boolean>(false);

  const { data: statusesData } = useGetData<IAppointmentStatus[]>(
    EndPoints.AppointmentStatuses,
  );
  const appointmentStatuses = [
    { Name: t("all"), Code: "ALL" },
    ...(statusesData?.Data ?? []),
  ];

  const [isCalenderOpen, setIsCalenderOpen] = useState<boolean>(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] =
    useState<boolean>(false);

  const methods = useForm({});

  const [selectedIds, setSelectedIds] = useState<any[]>([]);

  const { showToast } = useToast();
  const { mutate, isPending } = usePostData(EndPoints.CancelAppointment);

  const onSubmit = (values: { Note: string }) => {
    mutate(
      {
        Note: values?.Note,
        Ids: selectedIds,
      },
      {
        onSuccess: () => {
          setIsCancelConfirmOpen(false);
          setSelectedIds([]);
          setShowSelect(false);
          refetchAllAppointmentsApis(queryClient);
          showToast(t("appointment_canceled_successfully"), "success");
        },
        onError: (error) => {
          showToast(error.message, "error");
        },
      },
    );
  };

  useEffect(() => {
    setShowSelect(false);
  }, [filterByStatus]);
  const { searchAll } = useFilter();

  return (
    <div className="flex h-[94%] flex-col gap-4 lg:flex-row">
      <div className="box max-xl:hidden">
        <AppointmentsCalendar />
      </div>
      <Modal isOpen={isCalenderOpen} onClose={() => setIsCalenderOpen(false)}>
        <AppointmentsCalendar isFull />
      </Modal>
      <div className="box relative flex h-full w-full flex-col max-xl:mt-6">
        <div
          className="absolute -top-6 end-5 cursor-pointer rounded-t-lg bg-white px-2 xl:hidden"
          onClick={() => setIsCalenderOpen(true)}
        >
          <img src={calender} alt="calender" className="h-6 w-6" />
        </div>

        <IsProfileCompleted
          onAllowedClick={() => navigate("/appointments/add_new_appointment")}
        >
          <div className="absolute -top-6 end-20 cursor-pointer rounded-t-lg bg-primary px-2 xl:hidden">
            <img src={PlusIcon} alt="PlusIcon" className="h-6 w-6" />
          </div>
        </IsProfileCompleted>
        <div className="flex w-full flex-wrap justify-between gap-2 pb-3">
          <div className={`flex overflow-x-auto`}>
            {appointmentStatuses?.map((item, index) => {
              const isSelectedStatus = filterByStatus
                ? filterByStatus == item?.Code
                : item?.Code == "ALL";
              return (
                <div
                  key={index}
                  className={`nav-link w-fit cursor-pointer text-nowrap px-[11px] py-3 leading-3 ${isSelectedStatus ? "active font-bold" : "text-text-muted"}`}
                  onClick={() => {
                    currentLocationParams.set("filter", item?.Code);
                    const queryString = currentLocationParams.toString();
                    navigate(`?${queryString}`);
                  }}
                >
                  {item?.Name}
                </div>
              );
            })}
          </div>

          <div className="ms-auto flex gap-2">
            {showSelect ? (
              filterByStatus == "APPROVED" && (
                <>
                  <GeneralButton
                    disabled={selectedIds?.length == 0}
                    color="red"
                    onClick={() => {
                      setIsCancelConfirmOpen(true);
                    }}
                    title={
                      <span className="text-sm">
                        {isMobileOrTablet
                          ? t("cancel")
                          : t("select_to_cancel_appointment")}
                      </span>
                    }
                  />
                </>
              )
            ) : (
              <>
                {filterByStatus == "APPROVED" && (
                  <GeneralButton
                    disabled={appointmentsData?.length == 0}
                    color="red"
                    icon={close}
                    onClick={() => {
                      setShowSelect(true);
                    }}
                    title={
                      <span className="text-sm">
                        {isMobileOrTablet
                          ? t("cancel")
                          : t("select_to_cancel_appointment")}
                      </span>
                    }
                  />
                )}
                <IsProfileCompleted
                  onAllowedClick={() =>
                    navigate("/appointments/add_new_appointment")
                  }
                >
                  <GeneralButton
                    className="ms-auto !h-10 !rounded-lg !px-3 max-xl:hidden"
                    title={
                      <div className="flex items-center gap-1">
                        <img className="h-5 w-5" alt="plus" src={PlusIcon} />
                        <span className="text-sm">
                          {isMobileOrTablet
                            ? t("add")
                            : t("add_new_appointment")}
                        </span>
                      </div>
                    }
                  />
                </IsProfileCompleted>
              </>
            )}
          </div>
        </div>

        <FormProvider {...methods}>
          <form
            className="max-h-[70vh] flex-1 overflow-auto"
            onChange={(event: any) => {
              if (event?.target?.checked) {
                setSelectedIds((prev: any) => [
                  ...prev,
                  Number(event?.target?.name),
                ]);
              } else {
                setSelectedIds((prev: any) =>
                  prev.filter((id: any) => id != event?.target?.name),
                );
              }
            }}
          >
            <div className="flex h-full max-h-[70dvh] flex-1 flex-col gap-2 overflow-auto pb-10">
              <div className="flex h-fit w-full flex-col gap-y-2">
                {appointmentsData?.map((item: IAppointment, index: number) => {
                  const isPastDay = moment(item?.Date).isBefore(
                    moment(),
                    "day",
                  );
                  const isSelected = selectedIds?.includes(item?.Id);

                  return filterByStatus == "APPROVED" && showSelect ? (
                    !isPastDay ? (
                      <GeneralCheckbox
                        inputClasses="appointment-checkbox !w-[1.2rem] !h-[1.2rem] !rounded-md"
                        containerClasses={"!border-none"}
                        labelClassname="w-full"
                        name={`${item?.Id}`}
                        label={
                          <MyAppointmentCard
                            isClickable={false}
                            className={`cursor-pointer hover:bg-primary-bg ${isSelected ? "!bg-primary-bg" : ""}`}
                            appointment={item}
                            key={index}
                            isDisabled
                          />
                        }
                      />
                    ) : (
                      <div className="ps-8">
                        <MyAppointmentCard appointment={item} key={index} />
                      </div>
                    )
                  ) : (
                    <MyAppointmentCard appointment={item} key={index} />
                  );
                })}
              </div>

              <InfiniteScroll
                Count={6}
                skeletonCount={3}
                endpoint={EndPoints.GetMyAppointments}
                getAllParams={
                  filterByStatus && filterByStatus != "ALL"
                    ? {
                        Status: filterByStatus,
                        Date: selectedDate
                          ? moment(selectedDate)
                              .locale("en")
                              .format("yyyy-MM-D")
                          : undefined,
                      }
                    : {
                        Date: selectedDate
                          ? moment(selectedDate)
                              .locale("en")
                              .format("yyyy-MM-D")
                          : undefined,
                      }
                }
                getData={(data) => setAppointmentsData(data)}
                renderSkeleton={<MyAppointmentCardLoader />}
                noDataElement={
                  <>
                    <img
                      alt="empty appointment"
                      className="max-w-[30vw]"
                      src={EmptyImage}
                    />
                    <div className="text-lg text-body-text">
                      {t("no_appointments_in_this_day")}
                    </div>
                  </>
                }
              />
            </div>
          </form>
        </FormProvider>
      </div>
      <AppointmentDetailsDrawer
        onClose={() =>
          navigate(searchAll ? `/appointments${searchAll}` : `/appointments`)
        }
      />

      {isCancelConfirmOpen && (
        <AppointmentCancelConfirm
          status={"Cancel"}
          isOpen={isCancelConfirmOpen}
          onClose={() => {
            setIsCancelConfirmOpen(false);
          }}
          onSubmit={onSubmit}
          isLoading={isPending}
          multiple
        />
      )}
    </div>
  );
};

export default AppointmentsPage;
